import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('flux_token');
      const savedUser = localStorage.getItem('flux_user');
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('flux_user', JSON.stringify(response.data));
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('flux_token');
          localStorage.removeItem('flux_user');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []); // Empty deps is intentional - only run on mount

  const login = useCallback(async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('flux_token', access_token);
    localStorage.setItem('flux_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await authAPI.register({ email, password, name });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('flux_token', access_token);
    localStorage.setItem('flux_user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('flux_token');
    localStorage.removeItem('flux_user');
    setUser(null);
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem('flux_user', JSON.stringify(userData));
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateUser
  }), [user, loading, login, register, logout, updateUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
